import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getCases, getCaseById } from "@/api/caseApi";
import { assetUrl } from "@/utils/assetUrl";
import { readHomeCasesPreload } from "@/utils/homePreload";

function initialCases(): any[] {
  const preloaded = readHomeCasesPreload();
  if (!Array.isArray(preloaded)) return [];
  return preloaded.map((c: any) => ({
    ...c,
    industry: c.solutionName || c.industry || "",
    client: c.client || "",
    title: c.title || "",
    desc: c.summary || c.description || "",
    image: c.image || "",
    metrics: [],
  }));
}

export default function CasesSection() {
  const [cases, setCases] = useState<any[]>(initialCases);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    // 仅取列表数据，不再逐条请求详情（消除 N+1）
    getCases({ pageNum: 1, pageSize: 3 }).then((res: any) => {
      const rows = (res.rows || []).map((c: any) => ({
        ...c,
        industry: c.solutionName || c.industry || "",
        client: c.client || "",
        title: c.title || "",
        desc: c.summary || c.description || "",
        image: c.image || "",
        metrics: [], // 详情中的 metrics 子表按需懒加载
      }));
      setCases(rows);
    }).catch(() => {});
  }, []);

  // 按需懒加载当前展示案例的 metrics 子表数据（仅首次切到该案例时请求一次）
  useEffect(() => {
    const active = cases[current];
    if (!active || active._metricsLoaded) return;
    getCaseById(active.id).then((d: any) => {
      const metrics = (d.metrics || []).map((m: any) => ({ label: m.label, value: m.value }));
      setCases((prev) => prev.map((c, i) => i === current ? { ...c, metrics, _metricsLoaded: true } : c));
    }).catch(() => {
      setCases((prev) => prev.map((c, i) => i === current ? { ...c, _metricsLoaded: true } : c));
    });
  }, [current, cases]);

  const prev = () => setCurrent((c) => (c - 1 + cases.length) % cases.length);
  const next = () => setCurrent((c) => (c + 1) % cases.length);

  const activeCase = cases.length > 0 ? cases[current] : null;
  if (!activeCase) return null;

  return (
    <section className="py-10 lg:py-20 bg-white" id="cases">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
              <span className="text-[#1a56db] text-xs font-medium">成功案例</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0B1B3D] mb-3">
              欧格登已服务 <span className="text-[#1a56db]">500+</span> 企业客户
            </h2>
            <p className="text-gray-500 text-sm">覆盖金融、制造、政务、医疗、教育等多个行业的信息安全与基础架构项目</p>
          </div>
          <Link to="/cases" className="flex items-center gap-2 text-[#1a56db] text-sm font-medium hover:gap-3 transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0">
            查看全部案例
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
          </Link>
        </div>

        {/* Main Case Card */}
        <div className="bg-[#F8F9FA] rounded-2xl overflow-hidden border border-gray-100">
          <div className="flex flex-col lg:flex-row">
            {/* Left: Image */}
            <div className="w-full lg:w-[480px] flex-shrink-0 inline-flex flex-col">
              <div className="px-4 py-3 bg-white border-b border-gray-200">
                <div className="flex items-center gap-1.5 text-xs font-medium text-[#1a56db]">
                  <i className="ri-image-line text-sm"></i>
                  项目案例图
                </div>
              </div>
              <div className="w-full h-64 lg:h-80 overflow-hidden flex-1">
                <img src={assetUrl(activeCase.image)} alt="项目案例图" className="w-full h-full object-cover object-top transition-all duration-500" />
              </div>
              <div className="p-4 bg-white border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1.5 bg-[#1a56db]/8 text-[#1a56db] text-xs font-medium rounded-full">{activeCase.industry}</span>
                  <span className="text-gray-400 text-sm">{activeCase.client}</span>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1 p-8 lg:p-12 flex flex-col justify-between">
              <div>
                <h3 className="text-xl lg:text-2xl font-bold text-[#0B1B3D] mb-5 leading-snug">{activeCase.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{activeCase.desc}</p>

                {/* Metrics */}
                {activeCase.metrics.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {activeCase.metrics.map((m: any) => (
                      <div key={m.label} className="bg-white rounded-xl p-4 border border-gray-100 text-center">
                        <div className="text-xl lg:text-2xl font-bold text-[#1a56db] mb-1">{m.value}</div>
                        <div className="text-xs text-gray-400">{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button onClick={prev} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 hover:border-[#1a56db] hover:text-[#1a56db] text-gray-400 transition-all duration-200 cursor-pointer">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </button>
                  <div className="flex gap-1.5">
                    {cases.map((_, i) => (
                      <button key={i} onClick={() => setCurrent(i)} className={`transition-all duration-200 rounded-full cursor-pointer ${i === current ? "w-5 h-2 bg-[#1a56db]" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"}`} />
                    ))}
                  </div>
                  <button onClick={next} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 hover:border-[#1a56db] hover:text-[#1a56db] text-gray-400 transition-all duration-200 cursor-pointer">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                </div>
                <Link to={`/cases/${activeCase.id}`} className="text-xs text-[#1a56db] hover:underline cursor-pointer whitespace-nowrap">
                  查看完整案例 &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
